import { Navigate, Outlet } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useCompany } from '@/contexts/CompanyContext';
import { useNetUser } from '@/hooks/useNetUser';
import { useActiveOrgId } from '@/contexts/NetClientsContext';
import { idpEnabled } from '@/lib/idp';
import { PermissionKey } from '@mesterplan/shared-types';

interface ProtectedRouteProps {
  /** Single permission required */
  permission?: PermissionKey;
  /** Multiple permissions required */
  permissions?: PermissionKey[];
  /** If true, all permissions required. If false, any permission is sufficient */
  requireAll?: boolean;
  /** Path to redirect to if permission denied */
  fallbackPath?: string;
  /** If true, partner-only users bypass permission checks for this route */
  allowPartner?: boolean;
}

/**
 * Route guard component that checks permissions before rendering child routes.
 * Redirects to fallbackPath if user doesn't have required permission(s).
 *
 * @example
 * ```tsx
 * <Route element={<ProtectedRoute permission="projects.view" />}>
 *   <Route path="projects" element={<Projects />} />
 * </Route>
 *
 * // Multiple permissions (require all)
 * <Route element={<ProtectedRoute permissions={['users.view', 'users.edit']} />}>
 *   <Route path="users/edit" element={<EditUser />} />
 * </Route>
 *
 * // Multiple permissions (require any)
 * <Route element={<ProtectedRoute permissions={['reports.view', 'insights.view']} requireAll={false} />}>
 *   <Route path="analytics" element={<Analytics />} />
 * </Route>
 * ```
 */
export function ProtectedRoute({
  permission,
  permissions: requiredPermissions,
  requireAll = true,
  fallbackPath = '/',
  allowPartner,
}: ProtectedRouteProps) {
  const { hasPermission } = usePermissions();
  const { isPartnerUser, isLoadingCompanies, selectedCompany } = useCompany();
  const { isLoading: isUserLoading } = useNetUser();
  const activeOrgId = useActiveOrgId();

  // Don't redirect while still determining user type OR while the effective
  // permissions are still loading. Under IDP `hasPermission` is derived from
  // GET /api/users/me; redirecting before it resolves would bounce the first
  // nav to `/` even for a fully-permitted owner (the #772 symptom). No effect on
  // the Firebase path, where useNetUser is inert (isUserLoading stays false).
  if (isLoadingCompanies || isUserLoading) {
    return null;
  }

  // IDP: companies have resolved but no active org is selected yet — the brief
  // window NetClientsProvider renders routes while it picks the org from
  // /me/organizations. `permissions` is empty here, so a direct load/refresh of
  // a deep route would bounce to `/` if companies + /me #1 win that race. Hold
  // until the org resolves. `isPartnerUser` (no memberships) is the genuine
  // no-active-org case and is handled below via allowPartner, so exclude it.
  if (idpEnabled && activeOrgId === null && !isPartnerUser) {
    return null;
  }

  if (isPartnerUser && allowPartner) {
    return <Outlet />;
  }

  // Partner-tier company users can only access routes with allowPartner
  const isPartnerCompany = selectedCompany?.plan === 'partner';
  if (isPartnerCompany && !allowPartner) return <Navigate to={fallbackPath} replace />;

  // Determine if user has access
  let hasAccess = true;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (requiredPermissions && requiredPermissions.length > 0) {
    hasAccess = requireAll
      ? requiredPermissions.every((p) => hasPermission(p))
      : requiredPermissions.some((p) => hasPermission(p));
  }

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <Outlet />;
}
